import { GripVertical, Trash2, X } from "lucide-react";
import { lazy, Suspense, useCallback, useRef, useState } from "react";
import { Button } from "@/client/components/ui/button";
import { useAgent } from "@/client/hooks/use-agent";
import { ChatInput } from "./chat-input";
import { CostChip } from "./cost-chip";
import { MessageList } from "./message-list";
import { RestrictedBadge } from "./restricted-badge";

const MIN_WIDTH = 320;
const MAX_WIDTH = 700;
const DEFAULT_WIDTH = 420;

// Soft-import the persistence agent's ConversationList — don't crash if absent.
// The path is evaluated at runtime via a computed string so TS skips static
// module resolution checks.
const CONVERSATION_LIST_MODULE = "./conversation-list";
// biome-ignore lint/suspicious/noExplicitAny: soft optional dependency
type AnyFC = React.ComponentType<any>;

const ConversationList: AnyFC = lazy(
  () =>
    import(
      /* @vite-ignore */
      CONVERSATION_LIST_MODULE
    ).catch(() => ({ default: (): null => null })) as Promise<{ default: AnyFC }>,
);

export function AgentSidebar(): React.ReactElement | null {
  const {
    messages,
    isOpen,
    isStreaming,
    setIsOpen,
    sendMessage,
    approveProposal,
    rejectProposal,
    clearChat,
    dailyCostUsd,
    dailyCapUsd,
  } = useAgent();
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(DEFAULT_WIDTH);

  const isCapped = dailyCapUsd > 0 && dailyCostUsd >= dailyCapUsd;

  const handleSuggestionClick = useCallback(
    (text: string) => {
      if (!isStreaming && !isCapped) {
        void sendMessage(text);
      }
    },
    [isStreaming, isCapped, sendMessage],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = width;

      const onMouseMove = (ev: MouseEvent): void => {
        if (!isDragging.current) return;
        const delta = startX.current - ev.clientX;
        const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
        setWidth(newWidth);
      };

      const onMouseUp = (): void => {
        isDragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [width],
  );

  if (!isOpen) return null;

  return (
    <div
      className="relative flex flex-col border-l bg-background shrink-0 h-full overflow-hidden"
      style={{ width: `${width}px`, minWidth: `${MIN_WIDTH}px`, maxWidth: `${MAX_WIDTH}px` }}
      data-testid="agent-sidebar"
    >
      {/* Drag handle */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/20 active:bg-primary/40 z-10"
        onMouseDown={onMouseDown}
      >
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 rounded bg-border p-0.5 opacity-0 hover:opacity-100">
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </div>
      </div>

      {/* Header */}
      <div className="flex h-10 items-center justify-between border-b px-3 shrink-0">
        <h2 className="text-sm font-semibold">Assistant</h2>
        <div className="flex items-center gap-1.5">
          <CostChip />
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearChat}
              className="h-7 w-7"
              aria-label="Clear chat"
              data-testid="agent-clear-chat"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            className="h-7 w-7"
            aria-label="Close assistant"
            data-testid="agent-close-button"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Conversation history expander (persistence agent) */}
      {ConversationList && (
        <Suspense fallback={null}>
          <ConversationList />
        </Suspense>
      )}

      {/* Restricted sensitivity banner */}
      <RestrictedBadge messages={messages} />

      {/* Messages */}
      <MessageList
        messages={messages}
        isStreaming={isStreaming}
        onApproveProposal={approveProposal}
        onRejectProposal={rejectProposal}
        onSuggestionClick={handleSuggestionClick}
      />

      {/* Input */}
      <ChatInput onSend={sendMessage} disabled={isStreaming || isCapped} />
    </div>
  );
}
